import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  Club,
  ClubPaymentIntent,
  ClubPaymentIntentStatus,
  ClubWithdrawalDestination,
  ClubWithdrawalPixKeyType,
  ClubWithdrawalRequest,
  ClubWithdrawalRequestStatus,
  clubService,
} from '../services/clubService';
import '../styles/ClubWalletPortalPage.css';

const formatCurrency = (value: number): string =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(Number.isFinite(value) ? value : 0);

const formatDateTime = (value?: string | null): string => {
  if (!value) return '-';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '-';

  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(parsed);
};

const getStatusLabel = (status: ClubPaymentIntentStatus): string => {
  switch (status) {
    case 'PENDING':
      return 'Preparando';
    case 'AWAITING_PAYMENT':
      return 'Aguardando pagamento';
    case 'CONFIRMED':
      return 'Confirmado';
    case 'FAILED':
      return 'Falhou';
    case 'EXPIRED':
      return 'Expirado';
    case 'CANCELLED':
      return 'Cancelado';
    default:
      return status;
  }
};

const getStatusTone = (status: ClubPaymentIntentStatus): 'neutral' | 'warning' | 'success' | 'danger' => {
  switch (status) {
    case 'CONFIRMED':
      return 'success';
    case 'FAILED':
    case 'CANCELLED':
    case 'EXPIRED':
      return 'danger';
    case 'PENDING':
    case 'AWAITING_PAYMENT':
      return 'warning';
    default:
      return 'neutral';
  }
};

const getWithdrawalStatusLabel = (status: ClubWithdrawalRequestStatus): string => {
  switch (status) {
    case 'REQUESTED':
      return 'Solicitado';
    case 'UNDER_REVIEW':
      return 'Em revisao';
    case 'RESERVED':
      return 'Reservado';
    case 'PROCESSING':
      return 'Processando';
    case 'PAID':
      return 'Pago';
    case 'FAILED':
      return 'Falhou';
    case 'CANCELLED':
      return 'Cancelado';
    default:
      return status;
  }
};

const getWithdrawalTone = (
  status: ClubWithdrawalRequestStatus
): 'neutral' | 'warning' | 'success' | 'danger' => {
  switch (status) {
    case 'PAID':
      return 'success';
    case 'FAILED':
    case 'CANCELLED':
      return 'danger';
    case 'REQUESTED':
    case 'UNDER_REVIEW':
    case 'RESERVED':
    case 'PROCESSING':
      return 'warning';
    default:
      return 'neutral';
  }
};

const buildQrImageSrc = (encodedImage?: string | null): string => {
  if (!encodedImage) return '';
  if (encodedImage.startsWith('data:')) return encodedImage;
  return `data:image/png;base64,${encodedImage}`;
};

const BackIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <path d="m14 6-6 6 6 6" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const DepositIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <path d="M12 6v8" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    <path d="m8.5 10.5 3.5-3.5 3.5 3.5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    <path
      d="M6.5 15.5A2.5 2.5 0 0 1 9 13h6a2.5 2.5 0 0 1 2.5 2.5v1A2.5 2.5 0 0 1 15 19H9a2.5 2.5 0 0 1-2.5-2.5v-1Z"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinejoin="round"
    />
  </svg>
);

const WithdrawIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <path d="M12 18V10" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    <path d="m8.5 13.5 3.5 3.5 3.5-3.5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    <path
      d="M6.5 8.5A2.5 2.5 0 0 1 9 6h6a2.5 2.5 0 0 1 2.5 2.5v1A2.5 2.5 0 0 1 15 12H9a2.5 2.5 0 0 1-2.5-2.5v-1Z"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinejoin="round"
    />
  </svg>
);

const PRESET_AMOUNTS = [20, 50, 100, 200];
const WITHDRAWAL_PRESET_AMOUNTS = [20, 50, 100, 200];
const PIX_KEY_OPTIONS: Array<{ value: ClubWithdrawalPixKeyType; label: string }> = [
  { value: 'CPF', label: 'CPF' },
  { value: 'CNPJ', label: 'CNPJ' },
  { value: 'EMAIL', label: 'Email' },
  { value: 'PHONE', label: 'Telefone' },
  { value: 'EVP', label: 'Aleatoria' },
];

const PIX_KEY_FIELD_COPY: Record<
  ClubWithdrawalPixKeyType,
  {
    fieldLabel: string;
    placeholder: string;
    example: string;
    hint: string;
    inputMode: React.HTMLAttributes<HTMLInputElement>['inputMode'];
  }
> = {
  CPF: {
    fieldLabel: 'Chave Pix CPF',
    placeholder: 'Somente numeros, sem pontuacao',
    example: 'Ex.: 12345678901',
    hint: 'Use o CPF apenas se ele for a chave Pix realmente cadastrada no banco.',
    inputMode: 'numeric',
  },
  CNPJ: {
    fieldLabel: 'Chave Pix CNPJ',
    placeholder: 'Somente numeros, sem pontuacao',
    example: 'Ex.: 12345678000199',
    hint: 'Use o CNPJ apenas se ele for a chave Pix realmente cadastrada no banco.',
    inputMode: 'numeric',
  },
  EMAIL: {
    fieldLabel: 'Chave Pix Email',
    placeholder: 'email@exemplo.com',
    example: 'Ex.: financeiro@empresa.com',
    hint: 'Informe exatamente o email cadastrado como chave Pix.',
    inputMode: 'email',
  },
  PHONE: {
    fieldLabel: 'Chave Pix Telefone',
    placeholder: 'Somente 11 digitos com DDD',
    example: 'Ex.: 47999999999',
    hint: 'No Asaas, telefone deve ter 11 digitos ja com DDD e sem simbolos.',
    inputMode: 'tel',
  },
  EVP: {
    fieldLabel: 'Chave Pix Aleatoria',
    placeholder: 'UUID completo da chave aleatoria',
    example: 'Ex.: 550e8400-e29b-41d4-a716-446655440000',
    hint: 'Cole a chave aleatoria completa, exatamente como cadastrada no banco.',
    inputMode: 'text',
  },
};

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const UUID_LIKE_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const stripNonDigits = (value: string): string => value.replace(/\D/g, '');

const normalizePixKeyInput = (pixKeyType: ClubWithdrawalPixKeyType, rawValue: string): string => {
  const trimmed = rawValue.trim();

  switch (pixKeyType) {
    case 'CPF':
    case 'CNPJ':
    case 'PHONE':
      return stripNonDigits(trimmed);
    case 'EMAIL':
      return trimmed.toLowerCase();
    case 'EVP':
    default:
      return trimmed;
  }
};

const normalizeRecipientDocument = (rawValue: string): string => stripNonDigits(rawValue.trim());

const validateWithdrawalDestinationInput = (input: {
  pixKeyType: ClubWithdrawalPixKeyType;
  pixKey: string;
  recipientName: string;
  recipientDocument: string;
  currentPassword: string;
}): string | null => {
  const normalizedPixKey = normalizePixKeyInput(input.pixKeyType, input.pixKey);
  const normalizedDocument = normalizeRecipientDocument(input.recipientDocument);

  if (!normalizedPixKey) {
    return 'Informe a chave Pix completa antes de salvar.';
  }

  if (input.pixKeyType === 'CPF' && normalizedPixKey.length !== 11) {
    return 'A chave Pix do tipo CPF precisa ter 11 numeros.';
  }

  if (input.pixKeyType === 'CNPJ' && normalizedPixKey.length !== 14) {
    return 'A chave Pix do tipo CNPJ precisa ter 14 numeros.';
  }

  if (input.pixKeyType === 'PHONE' && normalizedPixKey.length !== 11) {
    return 'A chave Pix do tipo telefone precisa ter 11 numeros com DDD.';
  }

  if (input.pixKeyType === 'EMAIL' && !EMAIL_REGEX.test(normalizedPixKey)) {
    return 'Informe um email valido para a chave Pix.';
  }

  if (input.pixKeyType === 'EVP' && !UUID_LIKE_REGEX.test(normalizedPixKey)) {
    return 'A chave Pix aleatoria precisa estar no formato UUID completo.';
  }

  if (!input.recipientName.trim() || input.recipientName.trim().length < 3) {
    return 'Informe o nome completo do titular do destino Pix.';
  }

  if (!(normalizedDocument.length === 11 || normalizedDocument.length === 14)) {
    return 'Informe o CPF ou CNPJ completo do titular, somente numeros.';
  }

  if (!input.currentPassword.trim()) {
    return 'Confirme sua senha atual antes de salvar o destino Pix.';
  }

  return null;
};

type ClubWalletPortalPageProps = {
  mode: 'deposit' | 'withdraw';
};

const ClubWalletPortalPage: React.FC<ClubWalletPortalPageProps> = ({ mode }) => {
  const navigate = useNavigate();
  const { clubId } = useParams<{ clubId: string }>();
  const [club, setClub] = useState<Club | null>(null);
  const [intents, setIntents] = useState<ClubPaymentIntent[]>([]);
  const [withdrawalDestination, setWithdrawalDestination] = useState<ClubWithdrawalDestination | null>(null);
  const [withdrawalRequests, setWithdrawalRequests] = useState<ClubWithdrawalRequest[]>([]);
  const [detailIntent, setDetailIntent] = useState<ClubPaymentIntent | null>(null);
  const [detailWithdrawalRequest, setDetailWithdrawalRequest] = useState<ClubWithdrawalRequest | null>(null);
  const [amount, setAmount] = useState('50');
  const [withdrawalAmount, setWithdrawalAmount] = useState('50');
  const [withdrawalReason, setWithdrawalReason] = useState('');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [cancellingIntentId, setCancellingIntentId] = useState<string | null>(null);
  const [savingDestination, setSavingDestination] = useState(false);
  const [submittingWithdrawal, setSubmittingWithdrawal] = useState(false);
  const [cancellingWithdrawalId, setCancellingWithdrawalId] = useState<string | null>(null);
  const [message, setMessage] = useState<{ tone: 'success' | 'error'; text: string } | null>(null);
  const [editingWithdrawalDestination, setEditingWithdrawalDestination] = useState(false);
  const [destinationForm, setDestinationForm] = useState({
    pixKeyType: 'CPF' as ClubWithdrawalPixKeyType,
    pixKey: '',
    recipientName: '',
    recipientDocument: '',
    currentPassword: '',
  });
  const [withdrawalPassword, setWithdrawalPassword] = useState('');

  const activeIntent = useMemo(
    () => intents.find((intent) => intent.status === 'PENDING' || intent.status === 'AWAITING_PAYMENT') || null,
    [intents]
  );

  const activeWithdrawalRequest = useMemo(
    () =>
      withdrawalRequests.find((request) =>
        ['REQUESTED', 'UNDER_REVIEW', 'RESERVED', 'PROCESSING'].includes(request.status)
      ) || null,
    [withdrawalRequests]
  );

  const reservedWithdrawalBalance = useMemo(
    () =>
      withdrawalRequests
        .filter((request) => ['REQUESTED', 'UNDER_REVIEW', 'RESERVED', 'PROCESSING'].includes(request.status))
        .reduce((sum, request) => sum + Number(request.amount_reais || 0), 0),
    [withdrawalRequests]
  );

  const availableWalletBalance = useMemo(() => {
    const base = Number(club?.current_user_wallet_balance || 0);
    return Number(Math.max(base - reservedWithdrawalBalance, 0).toFixed(2));
  }, [club?.current_user_wallet_balance, reservedWithdrawalBalance]);

  const withdrawalDestinationLocked = useMemo(() => {
    if (!withdrawalDestination?.available_for_withdrawal_at) return false;
    return new Date(withdrawalDestination.available_for_withdrawal_at).getTime() > Date.now();
  }, [withdrawalDestination]);
  const selectedPixKeyCopy = PIX_KEY_FIELD_COPY[destinationForm.pixKeyType];

  const detailIntentAllowsPayment = detailIntent
    ? detailIntent.status === 'PENDING' || detailIntent.status === 'AWAITING_PAYMENT'
    : false;
  const isDepositMode = mode === 'deposit';
  const isWithdrawalMode = mode === 'withdraw';

  useEffect(() => {
    if (!detailIntent) return;

    const nextIntent = intents.find((intent) => intent.id === detailIntent.id) || null;
    if (!nextIntent) {
      setDetailIntent(null);
      return;
    }

    if (nextIntent !== detailIntent) {
      setDetailIntent(nextIntent);
    }
  }, [detailIntent, intents]);

  useEffect(() => {
    if (!detailWithdrawalRequest) return;

    const nextRequest = withdrawalRequests.find((request) => request.id === detailWithdrawalRequest.id) || null;
    if (!nextRequest) {
      setDetailWithdrawalRequest(null);
      return;
    }

    if (nextRequest !== detailWithdrawalRequest) {
      setDetailWithdrawalRequest(nextRequest);
    }
  }, [detailWithdrawalRequest, withdrawalRequests]);

  useEffect(() => {
    if (!withdrawalDestination) return;

    setDestinationForm((previous) => ({
      ...previous,
      pixKeyType: withdrawalDestination.pix_key_type,
      recipientName: withdrawalDestination.recipient_name || previous.recipientName,
    }));
  }, [withdrawalDestination]);

  useEffect(() => {
    setEditingWithdrawalDestination(!withdrawalDestination);
  }, [withdrawalDestination]);

  const loadPortal = useCallback(
    async (options?: { silent?: boolean }) => {
      if (!clubId) return;

      if (options?.silent) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      try {
        const [clubPayload, intentPayload, destinationPayload, withdrawalPayload] = await Promise.all([
          clubService.getClub(clubId),
          clubService.listMyDepositIntents(clubId, 12),
          clubService.getWithdrawalDestination(clubId),
          clubService.listMyWithdrawalRequests(clubId, 12),
        ]);

        setClub(clubPayload);
        setIntents(intentPayload);
        setWithdrawalDestination(destinationPayload);
        setWithdrawalRequests(withdrawalPayload);
      } catch (error: any) {
        setMessage({
          tone: 'error',
          text: error?.message || 'Nao foi possivel carregar a area financeira.',
        });
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [clubId]
  );

  useEffect(() => {
    void loadPortal();
  }, [loadPortal]);

  useEffect(() => {
    const shouldTrackActiveIntent = Boolean(
      activeIntent && (activeIntent.status === 'PENDING' || activeIntent.status === 'AWAITING_PAYMENT')
    );
    const shouldTrackActiveWithdrawal = Boolean(
      activeWithdrawalRequest && activeWithdrawalRequest.status === 'PROCESSING'
    );

    if (
      !shouldTrackActiveIntent &&
      !shouldTrackActiveWithdrawal
    ) {
      return undefined;
    }

    const timer = window.setInterval(() => {
      void loadPortal({ silent: true });
    }, 15000);

    return () => window.clearInterval(timer);
  }, [activeIntent, activeWithdrawalRequest, loadPortal]);

  const handleCreateDeposit = async () => {
    if (!clubId) return;

    if (activeIntent) {
      setMessage({
        tone: 'error',
        text: 'Voce ja tem um Pix pendente neste club. Use o pedido em aberto antes de gerar outro.',
      });
      return;
    }

    const numericAmount = Number(String(amount).replace(',', '.'));
    if (!Number.isFinite(numericAmount) || numericAmount <= 0) {
      setMessage({ tone: 'error', text: 'Informe um valor valido para gerar o Pix.' });
      return;
    }

    setSubmitting(true);
    setMessage(null);

    try {
      const intent = await clubService.createDepositIntent(clubId, {
        amountReais: numericAmount,
        billingType: 'PIX',
      });

      setIntents((previous) => [intent, ...previous.filter((entry) => entry.id !== intent.id)]);
      setMessage({
        tone: 'success',
        text: 'Pix gerado com sucesso. A carteira do club sera atualizada quando o Asaas confirmar o pagamento.',
      });
      await loadPortal({ silent: true });
    } catch (error: any) {
      setMessage({
        tone: 'error',
        text: error?.message || 'Nao foi possivel gerar o Pix agora.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleCopyPayload = async () => {
    const sourceIntent = detailIntentAllowsPayment ? detailIntent : activeIntent;
    if (!sourceIntent?.provider_qr_code_payload) return;

    try {
      await navigator.clipboard.writeText(sourceIntent.provider_qr_code_payload);
      setMessage({ tone: 'success', text: 'Codigo Pix copiado.' });
    } catch {
      setMessage({ tone: 'error', text: 'Nao foi possivel copiar o codigo Pix.' });
    }
  };

  const handleCancelIntent = async (intent: ClubPaymentIntent) => {
    if (!clubId) return;
    if (!(intent.status === 'PENDING' || intent.status === 'AWAITING_PAYMENT')) {
      return;
    }

    setCancellingIntentId(intent.id);
    setMessage(null);

    try {
      const updatedIntent = await clubService.cancelDepositIntent(clubId, intent.id);
      setIntents((previous) => previous.map((entry) => (entry.id === updatedIntent.id ? updatedIntent : entry)));
      setDetailIntent((previous) => (previous?.id === updatedIntent.id ? updatedIntent : previous));
      setMessage({
        tone: 'success',
        text: 'Pix cancelado. Voce ja pode gerar um novo deposito, se quiser.',
      });
      await loadPortal({ silent: true });
    } catch (error: any) {
      setMessage({
        tone: 'error',
        text: error?.message || 'Nao foi possivel cancelar o Pix agora.',
      });
    } finally {
      setCancellingIntentId(null);
    }
  };

  const handleSaveWithdrawalDestination = async () => {
    if (!clubId) return;

    const validationError = validateWithdrawalDestinationInput(destinationForm);
    if (validationError) {
      setMessage({ tone: 'error', text: validationError });
      return;
    }

    const normalizedPixKey = normalizePixKeyInput(destinationForm.pixKeyType, destinationForm.pixKey);
    const normalizedRecipientDocument = normalizeRecipientDocument(destinationForm.recipientDocument);

    setSavingDestination(true);
    setMessage(null);

    try {
      const destination = await clubService.upsertWithdrawalDestination(clubId, {
        pixKeyType: destinationForm.pixKeyType,
        pixKey: normalizedPixKey,
        recipientName: destinationForm.recipientName.trim(),
        recipientDocument: normalizedRecipientDocument,
        currentPassword: destinationForm.currentPassword,
      });

      setWithdrawalDestination(destination);
      setEditingWithdrawalDestination(false);
      setDestinationForm((previous) => ({
        ...previous,
        pixKey: '',
        recipientDocument: '',
        currentPassword: '',
      }));
      setMessage({
        tone: 'success',
        text:
          new Date(destination.available_for_withdrawal_at).getTime() > Date.now()
            ? 'Destino Pix atualizado. Ele entrou em cooldown de seguranca antes de poder ser usado no saque.'
            : 'Destino Pix salvo com sucesso.',
      });
      await loadPortal({ silent: true });
    } catch (error: any) {
      setMessage({
        tone: 'error',
        text: error?.message || 'Nao foi possivel salvar o destino Pix.',
      });
    } finally {
      setSavingDestination(false);
    }
  };

  const handleCreateWithdrawal = async () => {
    if (!clubId) return;

    if (activeWithdrawalRequest) {
      setMessage({
        tone: 'error',
        text: 'Voce ja possui uma solicitacao de saque em aberto neste club.',
      });
      return;
    }

    const numericAmount = Number(String(withdrawalAmount).replace(',', '.'));
    if (!Number.isFinite(numericAmount) || numericAmount <= 0) {
      setMessage({ tone: 'error', text: 'Informe um valor valido para o saque.' });
      return;
    }

    if (!withdrawalPassword.trim()) {
      setMessage({ tone: 'error', text: 'Confirme sua senha atual antes de solicitar o saque.' });
      return;
    }

    if (numericAmount > availableWalletBalance) {
      setMessage({
        tone: 'error',
        text: `Saldo disponivel insuficiente para saque. Disponivel: ${formatCurrency(availableWalletBalance)}.`,
      });
      return;
    }

    setSubmittingWithdrawal(true);
    setMessage(null);

    try {
      const request = await clubService.createWithdrawalRequest(clubId, {
        amountReais: numericAmount,
        currentPassword: withdrawalPassword,
        reason: withdrawalReason || undefined,
      });

      setWithdrawalRequests((previous) => [request, ...previous.filter((entry) => entry.id !== request.id)]);
      setWithdrawalPassword('');
      setWithdrawalReason('');
      setMessage({
        tone: 'success',
        text: 'Solicitacao de saque registrada. O valor ficou reservado ate a decisao do club.',
      });
      await loadPortal({ silent: true });
    } catch (error: any) {
      setMessage({
        tone: 'error',
        text: error?.message || 'Nao foi possivel registrar o saque agora.',
      });
    } finally {
      setSubmittingWithdrawal(false);
    }
  };

  const handleCancelWithdrawal = async (request: ClubWithdrawalRequest) => {
    if (!clubId) return;

    setCancellingWithdrawalId(request.id);
    setMessage(null);

    try {
      const updatedRequest = await clubService.cancelWithdrawalRequest(clubId, request.id);
      setWithdrawalRequests((previous) =>
        previous.map((entry) => (entry.id === updatedRequest.id ? updatedRequest : entry))
      );
      setMessage({
        tone: 'success',
        text: 'Solicitacao de saque cancelada e valor liberado para o saldo disponivel.',
      });
      await loadPortal({ silent: true });
    } catch (error: any) {
      setMessage({
        tone: 'error',
        text: error?.message || 'Nao foi possivel cancelar a solicitacao de saque.',
      });
    } finally {
      setCancellingWithdrawalId(null);
    }
  };

  if (loading) {
    return (
      <div className="wallet-portal-page">
        <div className="wallet-portal-page__texture" />
        <section className="wallet-portal-loading">
          <div className="wallet-portal-loading__line wallet-portal-loading__line--short" />
          <div className="wallet-portal-loading__line wallet-portal-loading__line--wide" />
          <div className="wallet-portal-loading__card" />
          <div className="wallet-portal-loading__card" />
        </section>
      </div>
    );
  }

  return (
    <div className="wallet-portal-page">
      <div className="wallet-portal-page__texture" />

      <header className="wallet-portal-topbar">
        <button
          type="button"
          className="wallet-portal-back"
          onClick={() => navigate(clubId ? `/clubs/${clubId}/wallet` : '/clubs')}
        >
          <BackIcon />
          <span>Voltar</span>
        </button>
        <div className="wallet-portal-topbar__actions">
          <Link
            to={clubId ? `/clubs/${clubId}/wallet/deposit` : '/clubs'}
            className={`wallet-portal-mode-link ${isDepositMode ? 'wallet-portal-mode-link--active' : ''}`}
          >
            <DepositIcon />
            <span>Deposito</span>
          </Link>
          <Link
            to={clubId ? `/clubs/${clubId}/wallet/withdraw` : '/clubs'}
            className={`wallet-portal-mode-link ${isWithdrawalMode ? 'wallet-portal-mode-link--active' : ''}`}
          >
            <WithdrawIcon />
            <span>Saque</span>
          </Link>
        </div>
      </header>

      <main className="wallet-portal-main">
        <section className="wallet-portal-summary">
          <article className="wallet-portal-card wallet-portal-card--balance wallet-portal-card--club">
            <div className="wallet-portal-card__row">
              <div>
                <span>Club</span>
                <strong>{club?.name || 'Club'}</strong>
              </div>
              <div className="wallet-portal-card__value">
                <span>Carteira neste club</span>
                <strong>{formatCurrency(availableWalletBalance)}</strong>
              </div>
            </div>
            {reservedWithdrawalBalance > 0 ? (
              <p className="wallet-portal-card__note">
                Reservado em saque: {formatCurrency(reservedWithdrawalBalance)}.
              </p>
            ) : null}
          </article>
        </section>

        {message && <div className={`wallet-portal-inline wallet-portal-inline--${message.tone}`}>{message.text}</div>}

        {isDepositMode && (
          <>
        <section className="wallet-portal-grid">
          <section className="wallet-portal-surface">
            <div className="wallet-portal-surface__header">
              <div>
                <p>Deposito externo</p>
                <h2>Gerar Pix</h2>
              </div>
              <span className="wallet-portal-tag">PIX</span>
            </div>

            <div className="wallet-portal-presets">
              {PRESET_AMOUNTS.map((preset) => (
                <button
                  key={preset}
                  type="button"
                  className={`wallet-portal-preset ${Number(amount) === preset ? 'wallet-portal-preset--active' : ''}`}
                  onClick={() => setAmount(String(preset))}
                  disabled={Boolean(activeIntent)}
                >
                  {formatCurrency(preset)}
                </button>
              ))}
            </div>

            <label className="wallet-portal-field">
              <span>Valor em reais</span>
              <input
                type="number"
                min="1"
                step="0.01"
                value={amount}
                onChange={(event) => setAmount(event.target.value)}
                placeholder="50"
                disabled={Boolean(activeIntent)}
              />
            </label>

            <button
              type="button"
              className="wallet-portal-primary"
              onClick={handleCreateDeposit}
              disabled={submitting || Boolean(activeIntent)}
            >
              {submitting ? 'Gerando Pix...' : activeIntent ? 'Pix pendente em aberto' : 'Gerar Pix externo'}
            </button>

            {activeIntent && (
              <div className="wallet-portal-inline wallet-portal-inline--warning">
                Existe um Pix em aberto para este club. Finalize ou aguarde a expiracao antes de gerar um novo.
              </div>
            )}

          </section>

          <section className="wallet-portal-surface">
            <div className="wallet-portal-surface__header">
              <div>
                <p>Pagamento em aberto</p>
                <h2>{activeIntent ? 'Pix ativo' : 'Nenhum Pix pendente'}</h2>
              </div>
              <button type="button" className="wallet-portal-refresh" onClick={() => void loadPortal({ silent: true })}>
                {refreshing ? 'Atualizando...' : 'Atualizar'}
              </button>
            </div>

            {activeIntent ? (
              <div className="wallet-portal-intent">
                <div className="wallet-portal-intent__summary">
                  <div>
                    <span>Valor</span>
                    <strong>{formatCurrency(Number(activeIntent.amount_reais || 0))}</strong>
                  </div>
                  <div className={`wallet-portal-status wallet-portal-status--${getStatusTone(activeIntent.status)}`}>
                    {getStatusLabel(activeIntent.status)}
                  </div>
                </div>

                <dl className="wallet-portal-meta">
                  <div>
                    <dt>Solicitado em</dt>
                    <dd>{formatDateTime(activeIntent.requested_at)}</dd>
                  </div>
                  <div>
                    <dt>Expira em</dt>
                    <dd>{formatDateTime(activeIntent.expires_at)}</dd>
                  </div>
                  <div>
                    <dt>Referencia</dt>
                    <dd>{activeIntent.external_reference || '-'}</dd>
                  </div>
                </dl>

                {activeIntent.provider_qr_code_image && (
                  <div className="wallet-portal-qr">
                    <img src={buildQrImageSrc(activeIntent.provider_qr_code_image)} alt="QR Code Pix" />
                  </div>
                )}

                <div className="wallet-portal-payload">
                  <textarea readOnly value={activeIntent.provider_qr_code_payload || ''} placeholder="O payload Pix aparecera aqui." />
                  <button type="button" onClick={handleCopyPayload} disabled={!activeIntent.provider_qr_code_payload}>
                    Copiar codigo Pix
                  </button>
                </div>

                {activeIntent.provider_invoice_url && (
                  <a className="wallet-portal-link" href={activeIntent.provider_invoice_url} target="_blank" rel="noreferrer">
                    Abrir cobranca externa
                  </a>
                )}

                <button
                  type="button"
                  className="wallet-portal-secondary wallet-portal-secondary--danger"
                  onClick={() => void handleCancelIntent(activeIntent)}
                  disabled={cancellingIntentId === activeIntent.id}
                >
                  {cancellingIntentId === activeIntent.id ? 'Cancelando Pix...' : 'Cancelar Pix'}
                </button>
              </div>
            ) : (
              <div className="wallet-portal-resolution wallet-portal-resolution--success">
                <strong className="wallet-portal-resolution__title">Nenhum Pix pendente neste momento.</strong>
                <p className="wallet-portal-resolution__hint">
                  Quando um deposito for confirmado, os detalhes completos ficam disponiveis ao tocar no item do historico abaixo.
                </p>
              </div>
            )}
          </section>
        </section>

        <section className="wallet-portal-surface">
          <div className="wallet-portal-surface__header">
            <div>
              <p>Historico recente</p>
              <h2>Depositos do jogador</h2>
            </div>
            <span className="wallet-portal-tag">{intents.length} itens</span>
          </div>

          <div className="wallet-portal-list">
            {intents.length === 0 ? (
              <div className="wallet-portal-empty">Nenhum deposito externo registrado neste club ainda.</div>
            ) : (
              intents.map((intent) => (
                <button
                  key={intent.id}
                  type="button"
                  className={`wallet-portal-list__item ${detailIntent?.id === intent.id ? 'wallet-portal-list__item--active' : ''}`}
                  onClick={() => setDetailIntent(intent)}
                >
                  <div className="wallet-portal-list__copy">
                    <strong>{formatCurrency(Number(intent.amount_reais || 0))}</strong>
                    <span>{formatDateTime(intent.requested_at)}</span>
                  </div>
                  <div className={`wallet-portal-status wallet-portal-status--${getStatusTone(intent.status)}`}>
                    {getStatusLabel(intent.status)}
                  </div>
                </button>
              ))
            )}
          </div>
        </section>
          </>
        )}

        {isWithdrawalMode && (
          <>
        <section className="wallet-portal-grid">
          <section className="wallet-portal-surface">
            <div className="wallet-portal-surface__header">
              <div>
                <p>Destino de saque</p>
                <h2>Pix validado do jogador</h2>
              </div>
              <span className="wallet-portal-tag">
                {withdrawalDestination ? withdrawalDestination.pix_key_type : 'PIX'}
              </span>
            </div>

            {withdrawalDestination ? (
              <div className="wallet-portal-resolution wallet-portal-resolution--success">
                <strong className="wallet-portal-resolution__title">Destino Pix atual pronto para saque.</strong>
                <div className="wallet-portal-data-grid">
                  <div className="wallet-portal-data-grid__item">
                    <span>Tipo da chave</span>
                    <strong>{withdrawalDestination.pix_key_type}</strong>
                  </div>
                  <div className="wallet-portal-data-grid__item">
                    <span>Chave Pix cadastrada</span>
                    <strong>{withdrawalDestination.pix_key_masked}</strong>
                  </div>
                  <div className="wallet-portal-data-grid__item">
                    <span>Titular</span>
                    <strong>{withdrawalDestination.recipient_name}</strong>
                  </div>
                  <div className="wallet-portal-data-grid__item">
                    <span>Documento do titular</span>
                    <strong>{withdrawalDestination.recipient_document_masked}</strong>
                  </div>
                </div>
                <dl className="wallet-portal-resolution__meta">
                  <div>
                    <dt>Ultima alteracao</dt>
                    <dd>{formatDateTime(withdrawalDestination.changed_at)}</dd>
                  </div>
                  <div>
                    <dt>Liberado para saque</dt>
                    <dd>{formatDateTime(withdrawalDestination.available_for_withdrawal_at)}</dd>
                  </div>
                </dl>
              </div>
            ) : (
              <div className="wallet-portal-inline wallet-portal-inline--warning">
                Cadastre um destino Pix antes de enviar o primeiro pedido de saque.
              </div>
            )}

            {withdrawalDestinationLocked && (
              <div className="wallet-portal-inline wallet-portal-inline--warning">
                O destino Pix entrou em cooldown de seguranca. Novos saques so poderao usar esta chave apos o horario liberado acima.
              </div>
            )}

            {!editingWithdrawalDestination && withdrawalDestination ? (
              <>
                <div className="wallet-portal-inline">
                  O destino fica em modo leitura para reduzir erro operacional. Abra a edicao somente se quiser trocar a chave usada no saque.
                </div>
                <div className="wallet-portal-card-actions">
                  <button
                    type="button"
                    className="wallet-portal-secondary"
                    onClick={() => {
                      setEditingWithdrawalDestination(true);
                      setDestinationForm((previous) => ({
                        ...previous,
                        pixKeyType: withdrawalDestination.pix_key_type,
                        recipientName: withdrawalDestination.recipient_name,
                        pixKey: '',
                        recipientDocument: '',
                        currentPassword: '',
                      }));
                    }}
                  >
                    Editar destino Pix
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="wallet-portal-guidance">
                  <h3>Antes de salvar</h3>
                  <ul>
                    <li>Informe a chave Pix real usada pelo banco do destinatario.</li>
                    <li>Documento do titular nao substitui a chave Pix.</li>
                  </ul>
                </div>

                <div className="wallet-portal-inline">
                  {withdrawalDestination
                    ? 'Voce esta trocando o destino atual. Informe a nova chave completa, confirme o documento do titular e valide com sua senha.'
                    : 'Cadastre um destino Pix uma unica vez. Depois disso, o portal mantem esse destino em modo leitura ate voce decidir editar.'}
                </div>

                <div className="wallet-portal-form-grid">
                  <label className="wallet-portal-field">
                    <span>Tipo da chave Pix</span>
                    <select
                      value={destinationForm.pixKeyType}
                      onChange={(event) =>
                        setDestinationForm((previous) => ({
                          ...previous,
                          pixKeyType: event.target.value as ClubWithdrawalPixKeyType,
                          pixKey: '',
                        }))
                      }
                      disabled={savingDestination}
                    >
                      {PIX_KEY_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </label>

                  <label className="wallet-portal-field">
                    <span>{withdrawalDestination ? `Nova ${selectedPixKeyCopy.fieldLabel}` : selectedPixKeyCopy.fieldLabel}</span>
                    <input
                      type="text"
                      inputMode={selectedPixKeyCopy.inputMode}
                      autoCapitalize="none"
                      autoCorrect="off"
                      spellCheck={false}
                      value={destinationForm.pixKey}
                      onChange={(event) =>
                        setDestinationForm((previous) => ({ ...previous, pixKey: event.target.value }))
                      }
                      placeholder={selectedPixKeyCopy.placeholder}
                      disabled={savingDestination}
                    />
                    <small className="wallet-portal-field__hint">
                      {selectedPixKeyCopy.hint} {selectedPixKeyCopy.example}
                    </small>
                  </label>

                  <label className="wallet-portal-field">
                    <span>Nome do titular</span>
                    <input
                      type="text"
                      value={destinationForm.recipientName}
                      onChange={(event) =>
                        setDestinationForm((previous) => ({ ...previous, recipientName: event.target.value }))
                      }
                      placeholder="Nome completo do titular"
                      disabled={savingDestination}
                    />
                  </label>

                  <label className="wallet-portal-field">
                    <span>CPF ou CNPJ do titular</span>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={destinationForm.recipientDocument}
                      onChange={(event) =>
                        setDestinationForm((previous) => ({ ...previous, recipientDocument: event.target.value }))
                      }
                      placeholder="Somente numeros"
                      disabled={savingDestination}
                    />
                    <small className="wallet-portal-field__hint">
                      Este campo identifica o titular. Ele nao substitui a chave Pix do campo acima.
                    </small>
                  </label>

                  <label className="wallet-portal-field wallet-portal-field--full">
                    <span>Confirme sua senha para salvar</span>
                    <input
                      type="password"
                      value={destinationForm.currentPassword}
                      onChange={(event) =>
                        setDestinationForm((previous) => ({ ...previous, currentPassword: event.target.value }))
                      }
                      placeholder="Senha atual"
                      disabled={savingDestination}
                    />
                  </label>
                </div>

                <div className="wallet-portal-card-actions">
                  {withdrawalDestination && (
                    <button
                      type="button"
                      className="wallet-portal-secondary"
                      onClick={() => {
                        setEditingWithdrawalDestination(false);
                        setDestinationForm((previous) => ({
                          ...previous,
                          pixKey: '',
                          recipientDocument: '',
                          currentPassword: '',
                          recipientName: withdrawalDestination.recipient_name,
                          pixKeyType: withdrawalDestination.pix_key_type,
                        }));
                      }}
                      disabled={savingDestination}
                    >
                      Manter destino atual
                    </button>
                  )}
                  <button
                    type="button"
                    className="wallet-portal-primary"
                    onClick={() => void handleSaveWithdrawalDestination()}
                    disabled={savingDestination}
                  >
                    {savingDestination
                      ? 'Salvando destino...'
                      : withdrawalDestination
                        ? 'Salvar alteracoes'
                        : 'Salvar destino Pix'}
                  </button>
                </div>
              </>
            )}
          </section>

          <section className="wallet-portal-surface">
            <div className="wallet-portal-surface__header">
              <div>
                <p>Saque externo</p>
                <h2>Solicitar retirada</h2>
              </div>
              <span className="wallet-portal-tag">Reserva segura</span>
            </div>

            <div className="wallet-portal-presets">
              {WITHDRAWAL_PRESET_AMOUNTS.map((preset) => (
                <button
                  key={preset}
                  type="button"
                  className={`wallet-portal-preset ${Number(withdrawalAmount) === preset ? 'wallet-portal-preset--active' : ''}`}
                  onClick={() => setWithdrawalAmount(String(preset))}
                  disabled={Boolean(activeWithdrawalRequest)}
                >
                  {formatCurrency(preset)}
                </button>
              ))}
            </div>

            <div className="wallet-portal-form-grid">
              <label className="wallet-portal-field">
                <span>Valor do saque</span>
                <input
                  type="number"
                  min="1"
                  step="0.01"
                  value={withdrawalAmount}
                  onChange={(event) => setWithdrawalAmount(event.target.value)}
                  placeholder="50"
                  disabled={Boolean(activeWithdrawalRequest)}
                />
              </label>

              <label className="wallet-portal-field">
                <span>Motivo ou observacao</span>
                <input
                  type="text"
                  value={withdrawalReason}
                  onChange={(event) => setWithdrawalReason(event.target.value)}
                  placeholder="Opcional"
                  disabled={Boolean(activeWithdrawalRequest)}
                />
              </label>

              <label className="wallet-portal-field wallet-portal-field--full">
                <span>Confirme sua senha para solicitar</span>
                <input
                  type="password"
                  value={withdrawalPassword}
                  onChange={(event) => setWithdrawalPassword(event.target.value)}
                  placeholder="Senha atual"
                  disabled={Boolean(activeWithdrawalRequest)}
                />
              </label>
            </div>

            <div className="wallet-portal-inline">
              Saldo disponivel para saque agora: {formatCurrency(availableWalletBalance)}.
            </div>

            {!withdrawalDestination && (
              <div className="wallet-portal-inline wallet-portal-inline--warning">
                Salve um destino Pix antes de solicitar o saque.
              </div>
            )}

            {withdrawalDestinationLocked && (
              <div className="wallet-portal-inline wallet-portal-inline--warning">
                O destino Pix ainda esta em cooldown. Aguarde a liberacao antes de pedir o saque.
              </div>
            )}

            {activeWithdrawalRequest && (
              <div className="wallet-portal-inline wallet-portal-inline--warning">
                Ja existe uma solicitacao em aberto neste club. Finalize esse ciclo antes de enviar outro pedido.
              </div>
            )}

            <button
              type="button"
              className="wallet-portal-primary"
              onClick={() => void handleCreateWithdrawal()}
              disabled={
                submittingWithdrawal ||
                Boolean(activeWithdrawalRequest) ||
                !withdrawalDestination ||
                withdrawalDestinationLocked
              }
            >
              {submittingWithdrawal
                ? 'Solicitando saque...'
                : activeWithdrawalRequest
                  ? 'Saque em analise'
                  : 'Solicitar saque'}
            </button>
          </section>
        </section>

        <section className="wallet-portal-grid">
          <section className="wallet-portal-surface">
            <div className="wallet-portal-surface__header">
              <div>
                <p>Saque</p>
                <h2>{activeWithdrawalRequest ? 'Pedido atual' : 'Nenhum saque aberto'}</h2>
              </div>
              <button type="button" className="wallet-portal-refresh" onClick={() => void loadPortal({ silent: true })}>
                {refreshing ? 'Atualizando...' : 'Atualizar'}
              </button>
            </div>

            {activeWithdrawalRequest ? (
              <div className="wallet-portal-intent">
                <div className="wallet-portal-intent__summary">
                  <div>
                    <span>Valor</span>
                    <strong>{formatCurrency(Number(activeWithdrawalRequest.amount_reais || 0))}</strong>
                  </div>
                  <div
                    className={`wallet-portal-status wallet-portal-status--${getWithdrawalTone(activeWithdrawalRequest.status)}`}
                  >
                    {getWithdrawalStatusLabel(activeWithdrawalRequest.status)}
                  </div>
                </div>

                <dl className="wallet-portal-meta">
                  <div>
                    <dt>Solicitado em</dt>
                    <dd>{formatDateTime(activeWithdrawalRequest.requested_at)}</dd>
                  </div>
                  <div>
                    <dt>Reserva</dt>
                    <dd>{activeWithdrawalRequest.reservation_status || '-'}</dd>
                  </div>
                  <div>
                    <dt>Referencia</dt>
                    <dd>{activeWithdrawalRequest.external_reference || '-'}</dd>
                  </div>
                </dl>

                <div className="wallet-portal-resolution wallet-portal-resolution--warning">
                  <strong className="wallet-portal-resolution__title">
                    Destino: {activeWithdrawalRequest.pix_key_snapshot || 'Destino salvo'}
                  </strong>
                  <p className="wallet-portal-resolution__hint">
                    Titular: {activeWithdrawalRequest.recipient_name_snapshot || '-'} . Documento:{' '}
                    {activeWithdrawalRequest.recipient_document_snapshot || '-'}.
                  </p>
                </div>

                {activeWithdrawalRequest.reason && (
                  <div className="wallet-portal-resolution">
                    <strong className="wallet-portal-resolution__title">Observacao enviada</strong>
                    <p className="wallet-portal-resolution__hint">{activeWithdrawalRequest.reason}</p>
                  </div>
                )}

                {['REQUESTED', 'UNDER_REVIEW', 'RESERVED'].includes(activeWithdrawalRequest.status) && (
                  <button
                    type="button"
                    className="wallet-portal-secondary wallet-portal-secondary--danger"
                    onClick={() => void handleCancelWithdrawal(activeWithdrawalRequest)}
                    disabled={cancellingWithdrawalId === activeWithdrawalRequest.id}
                  >
                    {cancellingWithdrawalId === activeWithdrawalRequest.id
                      ? 'Cancelando saque...'
                      : 'Cancelar solicitacao'}
                  </button>
                )}
              </div>
            ) : (
              <div className="wallet-portal-resolution wallet-portal-resolution--success">
                <strong className="wallet-portal-resolution__title">Nenhum saque aberto.</strong>
              </div>
            )}
          </section>

          <section className="wallet-portal-surface">
            <div className="wallet-portal-surface__header">
              <div>
                <p>Historico</p>
                <h2>Saques</h2>
              </div>
              <span className="wallet-portal-tag">{withdrawalRequests.length} itens</span>
            </div>

            <div className="wallet-portal-list">
              {withdrawalRequests.length === 0 ? (
                <div className="wallet-portal-empty">Sem solicitacoes registradas.</div>
              ) : (
                withdrawalRequests.map((request) => (
                  <button
                    key={request.id}
                    type="button"
                    className={`wallet-portal-list__item ${
                      detailWithdrawalRequest?.id === request.id ? 'wallet-portal-list__item--active' : ''
                    }`}
                    onClick={() => setDetailWithdrawalRequest(request)}
                  >
                    <div className="wallet-portal-list__copy">
                      <strong>{formatCurrency(Number(request.amount_reais || 0))}</strong>
                      <span>{formatDateTime(request.requested_at)}</span>
                    </div>
                    <div
                      className={`wallet-portal-status wallet-portal-status--${getWithdrawalTone(request.status)}`}
                    >
                      {getWithdrawalStatusLabel(request.status)}
                    </div>
                  </button>
                ))
              )}
            </div>
          </section>
        </section>
          </>
        )}

      </main>

      {detailIntent && (
        <div className="wallet-portal-modal" role="dialog" aria-modal="true">
          <div className="wallet-portal-modal__backdrop" onClick={() => setDetailIntent(null)} />
          <section className="wallet-portal-modal__sheet">
            <header className="wallet-portal-modal__header">
              <div>
                <p>Historico do deposito</p>
                <h2>Detalhes do pagamento</h2>
              </div>
              <button type="button" className="wallet-portal-modal__close" onClick={() => setDetailIntent(null)}>
                Fechar
              </button>
            </header>

            <div className="wallet-portal-modal__body">
              <div className="wallet-portal-intent__summary">
                <div>
                  <span>Valor</span>
                  <strong>{formatCurrency(Number(detailIntent.amount_reais || 0))}</strong>
                </div>
                <div className={`wallet-portal-status wallet-portal-status--${getStatusTone(detailIntent.status)}`}>
                  {getStatusLabel(detailIntent.status)}
                </div>
              </div>

              <dl className="wallet-portal-resolution__meta">
                <div>
                  <dt>Solicitado em</dt>
                  <dd>{formatDateTime(detailIntent.requested_at)}</dd>
                </div>
                <div>
                  <dt>Confirmado em</dt>
                  <dd>{formatDateTime(detailIntent.confirmed_at)}</dd>
                </div>
                <div>
                  <dt>Expirava em</dt>
                  <dd>{formatDateTime(detailIntent.expires_at)}</dd>
                </div>
                <div>
                  <dt>Referencia</dt>
                  <dd>{detailIntent.external_reference || '-'}</dd>
                </div>
                <div>
                  <dt>Pagamento Asaas</dt>
                  <dd>{detailIntent.provider_payment_id || '-'}</dd>
                </div>
                <div>
                  <dt>Metodo</dt>
                  <dd>{detailIntent.billing_type || 'PIX'}</dd>
                </div>
              </dl>

              {detailIntent.description && (
                <div className="wallet-portal-resolution">
                  <strong className="wallet-portal-resolution__title">Descricao</strong>
                  <p className="wallet-portal-resolution__hint">{detailIntent.description}</p>
                </div>
              )}

              {detailIntentAllowsPayment && detailIntent.provider_qr_code_image && (
                <div className="wallet-portal-qr">
                  <img src={buildQrImageSrc(detailIntent.provider_qr_code_image)} alt="QR Code Pix" />
                </div>
              )}

              {detailIntentAllowsPayment && (
                <div className="wallet-portal-payload">
                  <textarea readOnly value={detailIntent.provider_qr_code_payload || ''} />
                  <button type="button" onClick={handleCopyPayload} disabled={!detailIntent.provider_qr_code_payload}>
                    Copiar codigo Pix
                  </button>
                </div>
              )}

              {detailIntent.provider_invoice_url && (
                <a className="wallet-portal-link" href={detailIntent.provider_invoice_url} target="_blank" rel="noreferrer">
                  Abrir detalhes externos
                </a>
              )}

              {detailIntentAllowsPayment && (
                <button
                  type="button"
                  className="wallet-portal-secondary wallet-portal-secondary--danger"
                  onClick={() => void handleCancelIntent(detailIntent)}
                  disabled={cancellingIntentId === detailIntent.id}
                >
                  {cancellingIntentId === detailIntent.id ? 'Cancelando Pix...' : 'Cancelar Pix'}
                </button>
              )}
            </div>
          </section>
        </div>
      )}

      {detailWithdrawalRequest && (
        <div className="wallet-portal-modal" role="dialog" aria-modal="true">
          <div className="wallet-portal-modal__backdrop" onClick={() => setDetailWithdrawalRequest(null)} />
          <section className="wallet-portal-modal__sheet">
            <header className="wallet-portal-modal__header">
              <div>
                <p>Historico do saque</p>
                <h2>Detalhes da solicitacao</h2>
              </div>
              <button
                type="button"
                className="wallet-portal-modal__close"
                onClick={() => setDetailWithdrawalRequest(null)}
              >
                Fechar
              </button>
            </header>

            <div className="wallet-portal-modal__body">
              <div className="wallet-portal-intent__summary">
                <div>
                  <span>Valor</span>
                  <strong>{formatCurrency(Number(detailWithdrawalRequest.amount_reais || 0))}</strong>
                </div>
                <div
                  className={`wallet-portal-status wallet-portal-status--${getWithdrawalTone(detailWithdrawalRequest.status)}`}
                >
                  {getWithdrawalStatusLabel(detailWithdrawalRequest.status)}
                </div>
              </div>

              <dl className="wallet-portal-resolution__meta">
                <div>
                  <dt>Solicitado em</dt>
                  <dd>{formatDateTime(detailWithdrawalRequest.requested_at)}</dd>
                </div>
                <div>
                  <dt>Aprovado em</dt>
                  <dd>{formatDateTime(detailWithdrawalRequest.approved_at)}</dd>
                </div>
                <div>
                  <dt>Pago em</dt>
                  <dd>{formatDateTime(detailWithdrawalRequest.paid_at)}</dd>
                </div>
                <div>
                  <dt>Cancelado em</dt>
                  <dd>{formatDateTime(detailWithdrawalRequest.cancelled_at)}</dd>
                </div>
                <div>
                  <dt>Transferencia Asaas</dt>
                  <dd>{detailWithdrawalRequest.provider_transfer_id || '-'}</dd>
                </div>
                <div>
                  <dt>Reserva</dt>
                  <dd>{detailWithdrawalRequest.reservation_status || '-'}</dd>
                </div>
              </dl>

              <div className="wallet-portal-resolution">
                <strong className="wallet-portal-resolution__title">
                  Destino: {detailWithdrawalRequest.pix_key_snapshot || 'Pix salvo'}
                </strong>
                <p className="wallet-portal-resolution__hint">
                  Titular: {detailWithdrawalRequest.recipient_name_snapshot || '-'} . Documento:{' '}
                  {detailWithdrawalRequest.recipient_document_snapshot || '-'}.
                </p>
              </div>

              {detailWithdrawalRequest.reason && (
                <div className="wallet-portal-resolution">
                  <strong className="wallet-portal-resolution__title">Motivo informado</strong>
                  <p className="wallet-portal-resolution__hint">{detailWithdrawalRequest.reason}</p>
                </div>
              )}

              {detailWithdrawalRequest.failure_reason && (
                <div className="wallet-portal-resolution wallet-portal-resolution--danger">
                  <strong className="wallet-portal-resolution__title">Motivo de falha</strong>
                  <p className="wallet-portal-resolution__hint">{detailWithdrawalRequest.failure_reason}</p>
                </div>
              )}

              {['REQUESTED', 'UNDER_REVIEW', 'RESERVED'].includes(detailWithdrawalRequest.status) && (
                <button
                  type="button"
                  className="wallet-portal-secondary wallet-portal-secondary--danger"
                  onClick={() => void handleCancelWithdrawal(detailWithdrawalRequest)}
                  disabled={cancellingWithdrawalId === detailWithdrawalRequest.id}
                >
                  {cancellingWithdrawalId === detailWithdrawalRequest.id
                    ? 'Cancelando saque...'
                    : 'Cancelar solicitacao'}
                </button>
              )}
            </div>
          </section>
        </div>
      )}
    </div>
  );
};

export default ClubWalletPortalPage;
